import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { NetworkNextStep } from "@/components/NetworkNextStep";
import { withNetworkUtm } from "@/lib/network";
import { motion, type Variants } from "framer-motion";
import {
  breadcrumbSchema,
  personSchema,
  webPageSchema,
} from "@/lib/seo";

import { AlsoOnShrinkopedia } from "@/components/AlsoOnShrinkopedia";
const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function FounderOfTheShrinkNetwork() {
  const SEO_TITLE = "Founder of The Shrink Network | Shariq Refai, MD, MBA";
  const SEO_DESCRIPTION =
    "Why I built The Shrink Network: how shrinkMD led to a coordinated ecosystem of educational, clinical, and tool properties for mental health.";

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Founder of The Shrink Network", path: "/founder-of-the-shrink-network" },
  ];

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/founder-of-the-shrink-network"
        image="/og/founder-of-the-shrink-network.jpg"
        keywords="The Shrink Network, Shariq Refai founder, shrinkMD, shrinQ, Shrinkopedia, mental health ecosystem, telepsychiatry founder"
        schema={[
          personSchema,
          webPageSchema({
            path: "/founder-of-the-shrink-network",
            title: SEO_TITLE,
            description: SEO_DESCRIPTION,
            breadcrumbs,
          }),
          breadcrumbSchema(breadcrumbs),
        ]}
      />

      {/* Intro */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Founder of The Shrink Network" },
            ]}
          />
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="flex flex-col gap-5"
          >
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              Founder Perspective
            </span>
            <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
              Why I built The Shrink Network.
            </h1>
            <p className="text-lg md:text-xl text-secondary font-light leading-relaxed">
              The story behind a coordinated ecosystem of mental health
              properties.
            </p>
            <p className="text-sm text-muted-foreground tracking-wide">
              By Shariq Refai, MD, MBA &middot; Last reviewed May 15, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-li:font-light prose-li:text-secondary prose-strong:text-primary prose-strong:font-medium prose-a:text-accent hover:prose-a:text-primary">
          <h2>Where it started</h2>
          <p>I'm a psychiatrist. I founded shrinkMD because the wait to see a psychiatrist in most parts of the country is measured in months, not days, and I didn't want to keep telling patients that the right care was theoretically out there. shrinkMD is a multistate telepsychiatry practice. It treats individual patients. That's the clinical job, and it's still my day job.</p>
          <p>What I noticed almost immediately is that the work didn't end at the appointment. Patients had questions between visits. They wanted to understand the medication they were on. They wanted plain language about anxiety, depression, panic, OCD, sleep, and the thoughts that wouldn't quiet down. They wanted a way to work with the patterns when I wasn't in the room. The internet had a lot of writing about mental health and not very much of it was written by a clinician. That bothered me.</p>

          <h2>The first three sites came from one question</h2>
          <p>The question was: what would I actually want a patient to read tonight, between sessions, when they're trying to figure out what's going on?</p>
          <p>The honest answer wasn't one thing. It was three or four things, each with a different job.</p>
          <p>Sometimes the patient needed an explanation. What is generalized anxiety disorder. How does CBT actually work. What does a typical psychiatric visit look like. That's encyclopedia work, and that became Shrinkopedia.</p>
          <p>Sometimes the patient needed practical guidance. Not the encyclopedia entry on panic, but a way to think about the panic they're having at 2 a.m. That became AnxietyResource and DepressionResource.</p>
          <p>Sometimes the patient was holding a prescription bottle and didn't know what to expect. Plain language about the medication, the side effects, what to watch for, what discontinuation looks like. That became PsychiatryRx.</p>
          <p>Each of those is a different reader in a different moment. A single site that tries to do all of those jobs at once ends up doing none of them well. So I built them separately.</p>

          <h2>Why I kept going</h2>
          <p>I could have stopped at the first three. I didn't, because the work kept revealing more gaps.</p>
          <p>The next gap was the gap between understanding and doing. Reading about overthinking is not the same as working with it. The books I was writing covered the seeing part. They were less good at the doing part. So I built shrinQ as a structured educational system you can work through on your own. Not therapy. A framework I'd hand a patient between sessions, turned into a system that runs without a clinician in the room.</p>
          <p>Then the research gap. The clinical literature on anxiety is rich and not well translated for general readers. So AnxietyResearch became the evidence layer. It translates the studies and the data into plain language and ties them back to the rest of the network.</p>
          <p>Then the tools. Unstuck is a wellness app for the everyday moment when you need to interrupt a pattern. QuitScrolling addresses the attention pull that turns into anxiety for a lot of modern adults. These are practical tools, not clinical care.</p>
          <p>Then the profession itself. Most coverage of psychiatry is either dismissive or oversold. shrinkiatry is the site for the inside view of the field, written by someone who actually practices it.</p>
          <p>And underneath all of that, the founder site you're reading now, where the essays, books, and media live.</p>

          <h2>Why the sites are separated</h2>
          <p>The most common question I get is why these are different sites instead of one big site with categories.</p>
          <p>The answer is structural. A clinical practice and an educational publication need to be separate for legal and regulatory reasons. A self-guided program and an evidence repository serve different readers. A medication reference and a condition encyclopedia have different update schedules and different audiences. Mixing them creates conflicts and confusion. Keeping them separate keeps each one honest about its job.</p>
          <p>The other reason is reader behavior. Someone searching "panic attack symptoms" at 3 a.m. doesn't want a homepage that asks them to choose between care, education, research, and tools. They want the panic information. Specialized sites rank better, load faster, and waste less of the reader's time.</p>

          <h2>The clinical and the educational lines</h2>
          <p>I want to be clear about where the lines are.</p>
          <p><a href={withNetworkUtm("https://shrinkmd.com/", "founder_page_clinical_line")} target="_blank" rel="noopener noreferrer" className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors">shrinkMD (physician-led telepsychiatry practice)</a> is the clinical layer. It diagnoses, treats, prescribes, and follows individual patients. It's regulated as a healthcare provider. It's where actual care happens.</p>
          <p>Everything else in <a href={withNetworkUtm("https://shrinknetwork.com/", "founder_of_the_shrink_network_page")} rel="noopener" className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors">The Shrink Network</a> is educational. Shrinkopedia, Shrinktionary, AnxietyResource, DepressionResource, AnxietyResearch, PsychiatryRx, ShrinkDaily, shrinQ, Unstuck, QuitScrolling, shrinkiatry, shariqrefai.com. None of those provides medical advice. None of them establishes a doctor-patient relationship with the reader. They're written and reviewed by a psychiatrist (me), but reading them is not the same as being a patient.</p>
          <p>That distinction matters legally, and it matters clinically. If you're in crisis, the right move isn't an article. It's 988 or 911. If you need a clinical relationship, the right move is a clinician you can see, whether that's shrinkMD or someone else.</p>

          <h2>My role, page by page</h2>
          <ul>
            <li><strong>shrinkMD</strong> I'm the founder and a practicing psychiatrist there.</li>
            <li><strong><a href={withNetworkUtm("https://shrinkopedia.com/authors/shariq-refai/", "founder_editorial_role")} target="_blank" rel="noopener noreferrer" className="text-accent no-underline hover:underline">Shrinkopedia</a></strong> I'm the medical editor. Every entry is reviewed before publication.</li>
            <li><strong>AnxietyResource</strong> I'm the medical editor and a contributor.</li>
            <li><strong>DepressionResource</strong> I'm the medical editor.</li>
            <li><strong>AnxietyResearch</strong> I'm the medical editor and the named author on the evidence summaries and reports.</li>
            <li><strong>PsychiatryRx</strong> I'm the medical editor for the medication guides.</li>
            <li><strong>Shrinktionary</strong> I'm the medical editor. Every term is reviewed before publication, and Shrinktionary's Term Web organizes how those terms connect to each other so readers can walk from one concept to the next.</li>
            <li><strong>ShrinkDaily</strong> I'm the medical editor.</li>
            <li><strong>shrinQ</strong> I'm the founder and the author of the program content. The framework draws on my clinical background and the three books I wrote on this exact problem.</li>
            <li><strong>Unstuck</strong> I'm the founder.</li>
            <li><strong>QuitScrolling</strong> I'm the founder.</li>
            <li><strong>shrinkiatry</strong> I'm the founder and author. The flagship, The Psychiatry Operating Room, maps how the field is trained, paid, and practiced.</li>
            <li><strong>shariqrefai.com</strong> This is the founder site. The writing here is mine.</li>
          </ul>

          <h2>Why I disclose all of this</h2>
          <p>You have a right to know who built what and what they get paid for. shrinkMD is a clinical practice that earns clinical revenue. shrinQ is a commercial product. The books are commercial books. The educational publications don't run ads and don't take affiliate commissions, by design.</p>
          <p>I think disclosing financial interests is more useful than pretending no one has any. So I disclose them plainly, on every site, every time.</p>

          <h2>What you do with this</h2>
          <p>If you're a reader, you don't need to know the org chart to use the work. Pick the site that fits the question and you'll get to the right thing.</p>
          <p>If you're a clinician, journalist, or partner, this page is here to give you the full picture in one place.</p>
          <p>If you're a patient looking for care, that's shrinkMD. If you're looking for understanding, that's Shrinkopedia. If you're looking for a structured way to work with overthinking, that's shrinQ. If you're looking for me, you're already here.</p>
        </div>
      </article>

      {/* Continue across the network */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkRouter
            eyebrow="Continue across the network"
            cards={[
              { label: "See the full Network", href: withNetworkUtm("https://shrinknetwork.com/", "founder_page_continue") },
              { label: "Browse the founder projects", href: "/projects" },
              { label: "Read the medical reviewer profile", href: "/medical-reviewer" },
              { label: "Read the books", href: "/books" },
            ]}
          />
        </div>
      </section>

      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="founder_page_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>
    
      <section className="bg-background py-16 md:py-20 px-4 md:px-8 border-t border-border">
        <div className="container mx-auto max-w-3xl">
          <AlsoOnShrinkopedia slug="founder-of-the-shrink-network" variant="credentialing" />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/about", label: "About Shariq Refai, MD, MBA", why: "The biography behind the network's founder." },
          { href: "/projects", label: "Projects founded, built, and reviewed", why: "The full list of ventures and publications inside the network." },
          { href: "/what-im-building", label: "What I'm building at The Shrink Network", why: "Current work in progress across the ecosystem." },
          { href: "/disclosures", label: "Relationships & financial disclosure", why: "How ownership, revenue, and editorial roles are separated." },
        ]}
      />
    </SiteLayout>
  );
}
